import { useState, useEffect } from 'react';
import { sms as smsApi } from '../api/client.js';

const STATUS_STYLES = {
  sent: 'bg-green-200 text-green-800',
  delivered: 'bg-green-200 text-green-800',
  simulated: 'bg-yellow-200 text-yellow-800',
  failed: 'bg-red-200 text-red-800',
};

const STATUS_LABELS = {
  sent: 'Envoyé',
  delivered: 'Livré',
  simulated: 'Simulé',
  failed: 'Échoué',
};

/**
 * SMS log viewer with optional client filter
 */
export default function SmsLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filter) params.clientId = filter;
      const data = await smsApi.log(params);
      setLogs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Get unique clients for filter dropdown
  const uniqueClients = [...new Map(
    logs.filter(l => l.client_name).map(l => [l.client_id, l.client_name])
  ).entries()].map(([id, name]) => ({ id, name }));

  const handleFilter = (clientId) => {
    setFilter(clientId);
    // Reload with new filter
    setLoading(true);
    const params = clientId ? { clientId } : {};
    smsApi.log(params)
      .then(setLogs)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Historique SMS</h2>

      {/* Filter */}
      {uniqueClients.length > 0 && (
        <select
          value={filter}
          onChange={(e) => handleFilter(e.target.value)}
          className="mb-4 px-3 py-2 border rounded-lg text-sm w-full"
        >
          <option value="">Tous les clients</option>
          {uniqueClients.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      )}

      {error && <p className="text-red-600 mb-4">{error}</p>}

      {loading ? (
        <p className="text-gray-500 text-center py-8">Chargement...</p>
      ) : logs.length === 0 ? (
        <p className="text-gray-400 text-center py-8">Aucun SMS envoyé.</p>
      ) : (
        <div className="space-y-2">
          {logs.map((log) => (
            <div key={log.id} className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-start mb-1">
                <span className="font-medium text-sm">{log.client_name || log.phone}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[log.status] || 'bg-gray-200'}`}>
                  {STATUS_LABELS[log.status] || log.status}
                </span>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2">{log.message_text}</p>
              <div className="flex justify-between text-xs text-gray-400 mt-2">
                <span>{log.template === 'after_delivery' ? 'Après livraison' : log.template === 'before_delivery' ? 'Avant livraison' : log.template}</span>
                <span>{new Date(log.sent_at).toLocaleString('fr-FR')}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
